import {Plural, Trans} from '@lingui/macro';
import React, {FC, useCallback} from 'react';

import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {findConversationNamespaces} from '../../../../../helpers/models/conversationHelpers';
import {PermissionResult} from '../../../../../helpers/permissionHelpers';
import {ConversationBaseModel} from '../../../../../models/api/conversationModel';
import {PermissionsEnum} from '../../../../../models/api/roleModel';
import {OkCancelDialog} from '../../../../common/dialogs/okCancelDialog';
import {PermissionPopoverCoordinator} from '../../../../common/popovers/permissionPopoverCoordinator';

/*
 * Props.
 */

interface ConversationTopbarDestroyProps {
  conversations: ReadonlyArray<ConversationBaseModel>;
  bulkActionPermission: PermissionResult;
  onDestroyClick: Handler;
}

/*
 * Component.
 */

export const ConversationTopbarDestroy: FC<ConversationTopbarDestroyProps> = (props) => (
  <PermissionPopoverCoordinator
    namespaces={findConversationNamespaces(props.conversations)}
    permission={PermissionsEnum.CONVERSATION_DESTROY}
    bulkActionPermission={props.bulkActionPermission}
    renderButton={renderButton}
    renderDisabledButton={renderDisabledButton}
    renderModal={(popoverProps) => <DestroyConfirmationDialog {...popoverProps} {...props} />}
  />
);

/*
 * Button.
 */

function renderButton(isOpen: boolean) {
  return (
    <Button variant={ButtonVariantsEnum.SECONDARY} size={VisualSizesEnum.MEDIUM} hasHover={isOpen}>
      <Trans id="conversation-topbar-deletepermanently">Delete permanently</Trans>
    </Button>
  );
}

function renderDisabledButton() {
  return (
    <Button variant={ButtonVariantsEnum.SECONDARY} size={VisualSizesEnum.MEDIUM} isDisabled>
      <Trans id="conversation-topbar-deletepermanently">Delete permanently</Trans>
    </Button>
  );
}

/*
 * Confirmation dialog.
 */

interface DestroyConfirmationDialogProps {
  conversations: ReadonlyArray<ConversationBaseModel>;
  onDestroyClick: Handler;
}

export const DestroyConfirmationDialog: FC<
  Omit<PopoverTransferProps, 'anchor'> & DestroyConfirmationDialogProps
> = (props) => {
  const {conversations, onRequestClose, onDestroyClick} = props;
  const onDestroyOkClick = useCallback(() => {
    onRequestClose();
    onDestroyClick();
  }, [onDestroyClick, onRequestClose]);

  return (
    <OkCancelDialog
      {...props}
      width={500}
      title={<Trans id="conversation-deletepermanently-title">Delete permanently</Trans>}
      okButtonLabel={<Trans id="conversation-deletepermanently-ok">Delete</Trans>}
      okButtonVariant={ButtonVariantsEnum.DANGER}
      onOkClick={onDestroyOkClick}
    >
      <Plural
        id="conversation-deletepermanently-content"
        value={conversations.length}
        one={
          <>
            This conversation will be permanently deleted from Front.
            <br />
            This <strong>cannot</strong> be undone.
          </>
        }
        other={
          <>
            These <strong>{conversations.length}</strong> conversations will be permanently deleted from
            Front.
            <br /> This <strong>cannot</strong> be undone.
          </>
        }
      />
    </OkCancelDialog>
  );
};
